import { IssueTaskDoc, OrderDoc, StoreIssueDoc, TaskOrderCollection } from '@gooduncles/gu-app-schema';
import { Popover } from 'antd';
import { FC, useMemo } from 'react';

import { formatNumber } from 'src/utils/common';
import { TaskSpot } from 'src/utils/partners-util';

import classes from './TaskSpotCard.module.scss';

import IssueTaskBadge from './IssueTaskBadge';
import SortKeyBadge from './SortKeyBadge';
import StoreIssueBadge from './StoreIssueBadge';

interface TaskSpotCardProps {
  title: string;
  data: TaskSpot;
  showDetail: boolean;
  showSortKey?: boolean;
}

const TaskSpotCard: FC<TaskSpotCardProps> = ({ title, data, showDetail, showSortKey = false }) => {
  const storeList = data.stores.map((item) => {
    const orders = item.taskBundles
      .filter((bundle) => bundle.orderColleciton === TaskOrderCollection.Order)
      .map((bundle) => bundle.data as OrderDoc);
    const orderAmount = orders.reduce((acc, cur) => acc + cur.totalAmount, 0);
    const orderCount = orders.length;
    const storeIssues = item.taskBundles
      .filter((bundle) => bundle.orderColleciton === TaskOrderCollection.StoreIssue)
      .map((bundle) => bundle.data as StoreIssueDoc);
    const issueTasks = item.taskBundles
      .filter((bundle) => bundle.orderColleciton === TaskOrderCollection.IssueTask)
      .map((bundle) => bundle.data as IssueTaskDoc);
    const issueCount = storeIssues.length + issueTasks.length;
    return {
      store: item.store,
      amount: orderAmount,
      orderCount,
      issueCount,
      storeIssues,
      issueTasks,
      orders,
    };
  });

  const orderCount = storeList.reduce((acc, cur) => acc + cur.orderCount, 0);
  const issueCount = storeList.reduce((acc, cur) => acc + cur.issueCount, 0);

  const totalAmount = storeList.reduce((acc, cur) => acc + cur.amount, 0);

  const orderPopoverContent = useMemo(() => {
    return (
      <div className={classes.orderPopoverContent}>
        {storeList
          .filter((item) => item.orders.length > 0)
          .map((item) => (
            <div key={item.store?._id ?? 'unregistered'} className={classes.orderPopoverStore}>
              <p className={classes.orderPopoverStoreName}>{item.store?.storeNickname ?? 'unregistered'}</p>
              {item.orders.map((order) => (
                <div key={order._id} className={classes.orderPopoverProducts}>
                  {[...order.products]
                    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'ko'))
                    .map((product, idx) => (
                      <p key={idx}>
                        <span className={classes.productName}>{product.fullName}</span>
                        <span className={classes.productVolume}>{product.volume}</span>
                      </p>
                    ))}
                </div>
              ))}
            </div>
          ))}
      </div>
    );
  }, [storeList]);

  return (
    <div className={classes.spotCard}>
      {showSortKey && data.deliverySpot?.sortKeyForBadge && (
        <SortKeyBadge sortKey={data.deliverySpot.sortKeyForBadge} />
      )}
      <div className={classes.cardTitle}>
        <h4>
          {title}({storeList.length})
        </h4>
        <span>{formatNumber(totalAmount)}</span>
      </div>
      {showDetail && (
        <>
          {storeList.length > 0 &&
            storeList.map((summary) => (
              <div key={summary.store?._id ?? 'unregistered'} className={classes.summary}>
                <p className={summary.store ? '' : classes.unregistered}>
                  • {summary.store?.storeNickname ?? 'unregistered'}
                  {summary.orderCount ? (
                    <span className={classes.orderAmount}>({formatNumber(summary.amount)})</span>
                  ) : (
                    ''
                  )}
                </p>
                {(summary.storeIssues.length > 0 || summary.issueTasks.length > 0) && (
                  <div>
                    {summary.storeIssues.map((issue) => (
                      <StoreIssueBadge key={issue._id} issue={issue} />
                    ))}
                    {summary.issueTasks.map((task) => (
                      <IssueTaskBadge key={task._id} task={task} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          <div className={classes.amount}>
            <span>
              {orderCount} {issueCount > 0 && <span className={classes.issueCount}>({issueCount})</span>}
            </span>
            <Popover content={orderPopoverContent} title="주문 상품">
              <span className={classes.totalAmount} onClick={(e) => e.stopPropagation()}>
                {formatNumber(totalAmount)}
              </span>
            </Popover>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskSpotCard;
